import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { ConfirmModal } from "@/components/ConfirmModal.tsx";
import { CopyButton } from "@/components/CopyButton.tsx";
import { Trash2 } from "lucide-react";
import { UnauthorizedError, apiGet } from "@/lib/api.ts";
import { AuthApiError, type OrgInvitation, cancelInvitation, listInvitations } from "@/lib/auth.ts";
import { relativeTime } from "@/lib/format.ts";

// FRE-279: pending invitations admin view.

type MeOrg = { id: string; slug: string; name: string; role: string };
type Me = {
  userId: string;
  organizationId: string;
  user: { id: string; name: string | null; email: string } | null;
  organizations: MeOrg[];
};

function isAdminRole(role: string): boolean {
  return role === "owner" || role === "admin";
}

export const Route = createFileRoute("/org/invitations")({
  beforeLoad: async () => {
    try {
      const me = await apiGet<Me>("/api/v1/me");
      return { me };
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        throw redirect({ to: "/login", search: { next: "/org/invitations" } });
      }
      throw err;
    }
  },
  loader: async ({ context }) => context,
  component: InvitationsComponent,
});

function InvitationsComponent() {
  const { me } = Route.useLoaderData();
  const activeOrg = me.organizations.find((o) => o.id === me.organizationId) ?? null;
  const isAdmin = activeOrg ? isAdminRole(activeOrg.role) : false;

  const [items, setItems] = useState<OrgInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listInvitations(activeOrg.id);
      // Show only invitations that are still actionable. Better Auth marks
      // accepted/cancelled/rejected status server-side; we filter client-
      // side to keep the table focused on pending work.
      const pending = res.filter((i) => i.status === "pending");
      setItems(pending);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load invitations");
    } finally {
      setLoading(false);
    }
  }, [activeOrg]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!activeOrg) {
    return (
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-semibold mb-4">Invitations</h1>
        <p className="text-sm text-muted-foreground">
          You don't have an active organization.{" "}
          <Link to="/orgs/new" className="text-primary hover:underline">
            Create one
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Invitations</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {activeOrg.name}
            <span className="mx-1.5 text-muted-foreground/40">·</span>
            <Link to="/org/members" className="text-sm text-primary hover:underline">
              ← Members
            </Link>
          </p>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Retry
          </button>
        </div>
      ) : null}

      {actionError ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="rounded-md border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-4 py-2 border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <div>Email</div>
          <div>Role</div>
          <div>Sent</div>
          <div aria-hidden="true" className="w-40" />
        </div>
        {loading ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">
            No pending invitations.
          </div>
        ) : (
          items.map((inv) => (
            <InvitationRow
              key={inv.id}
              invitation={inv}
              isAdmin={isAdmin}
              onChange={() => void load()}
              onError={setActionError}
            />
          ))
        )}
      </div>
    </div>
  );
}

function InvitationRow({
  invitation,
  isAdmin,
  onChange,
  onError,
}: {
  invitation: OrgInvitation;
  isAdmin: boolean;
  onChange: () => void;
  onError: (msg: string) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Invitation `id` doubles as the accept token in Better Auth's current
  // schema. We use the same value in the public accept URL so the recipient
  // can just click the link.
  const link = `${window.location.origin}/invitations/${invitation.id}`;

  // Use `createdAt` if Better Auth surfaces it; otherwise derive a relative
  // "expires in" hint from `expiresAt` so the row still has a useful timestamp.
  const sent = invitation.createdAt
    ? relativeTime(invitation.createdAt)
    : invitation.expiresAt
      ? `expires ${relativeTime(invitation.expiresAt)}`
      : "—";

  async function handleRevoke() {
    setBusy(true);
    try {
      await cancelInvitation(invitation.id);
      setConfirmOpen(false);
      onChange();
    } catch (err) {
      const msg = err instanceof AuthApiError ? err.message : "Failed to revoke";
      onError(msg);
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-4 py-3 border-b border-border last:border-b-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{invitation.email}</div>
      </div>
      <div>
        <span className="text-xs font-medium text-foreground/80 uppercase tracking-wide">
          {invitation.role ?? "member"}
        </span>
      </div>
      <div className="text-xs text-muted-foreground whitespace-nowrap">{sent}</div>
      <div className="flex items-center justify-end gap-2 w-40">
        <CopyButton value={link} label="Copy link" size="xs" />
        {isAdmin ? (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            aria-label={`Revoke invitation for ${invitation.email}`}
            className="inline-flex items-center gap-1 rounded-md border border-input px-1.5 py-0.5 text-[11px] font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
          >
            <Trash2 className="size-4" />
            Revoke
          </button>
        ) : null}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={`Revoke invitation for ${invitation.email}?`}
        body={
          <p>
            The recipient won't be able to accept this invitation anymore. You can re-invite them
            later.
          </p>
        }
        confirmLabel="Revoke invitation"
        danger
        busy={busy}
        onConfirm={() => void handleRevoke()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
